import { css } from 'lit';
import JhaStyleElement from '../../style-element/JhaStyleElement.js';
import { jhaStyles } from '../../styles/jha-styles.js';

/** @typedef {import('../jha-slider-pane/JhaSliderPane.js').default} JhaSliderPane */
/**
 * A JHA Design slider content component. Manages the focused pane in a jha-slider.
 * @element jha-slider-content
 * @prop {string} focusedPane - the currently focused pane.
 */
export default class JhaSliderContent extends JhaStyleElement {
  static get styles() {
    return [
      jhaStyles,
      css`
        :host {
          display: flex;
          width: 100%;
          position: relative;
          transition: min-height ease 0.4s;
        }
        :host([full-height]) {
          height: 100%;
        }
      `,
    ];
  }

  static get properties() {
    return {
      focusedPane: {
        type: String,
      },
      staticHeight: {
        type: Boolean,
        value: false,
      },
      fullHeight: {
        type: Boolean,
        value: false,
        reflect: true,
        attribute: 'full-height',
      },
    };
  }

  constructor() {
    super();
    this.focusedPane = null;
    this.staticHeight = false;
    this.fullHeight = false;
  }

  updated(changedProperties) {
    if (changedProperties.has('focusedPane')) {
      this.focusPane(this.focusedPane);
    }
  }

  focusPane(attribute) {
    if (!attribute) {
      return;
    }
    const panes = Array.from(this.querySelectorAll('jha-slider-pane'));

    if (!panes) {
      return;
    }
    /** @type {JhaSliderPane} */
    let currentFocus = this.querySelector('jha-slider-pane[focus]');
    if (!currentFocus) {
      currentFocus = this.querySelector('jha-slider-pane[one]');
    }
    // try alternative attribute
    /** @type {JhaSliderPane} */
    let newFocus = this.querySelector(
      `jha-slider-pane[pane-id="${attribute}"]`
    );
    // try regular attribute (backward compatible)
    if (!newFocus && attribute) {
      newFocus = this.querySelector(`jha-slider-pane[${attribute}]`);
    }

    if (currentFocus === newFocus) {
      return;
    }

    // ignore if no newFocus
    if (!newFocus) {
      return;
    }

    // figure out the indices of the current and next slider panes
    const currentIndex = panes.indexOf(currentFocus);
    const newIndex = panes.indexOf(newFocus);

    if (currentFocus) {
      // if the new slider pane is logically to the right of current one,
      // slide the current pane to the left
      if (newIndex > currentIndex) {
        currentFocus.setAttribute('left', '');
        currentFocus.removeAttribute('right');
      } else {
        currentFocus.setAttribute('right', '');
        currentFocus.removeAttribute('left');
      }
      currentFocus.removeAttribute('focus');
      currentFocus.style.position = '';
    }

    if (newFocus) {
      if (!this.fullHeight) {
        const oldHeight =
          currentFocus && currentFocus.getBoundingClientRect().height;
        const newHeight = newFocus.getBoundingClientRect().height;
        if (this.staticHeight && oldHeight) {
          this.style.minHeight = `${Math.max(oldHeight, newHeight)}px`;
        } else {
          this.style.minHeight = `${newHeight}px`;
        }
      }
      newFocus.setAttribute('focus', '');
    }
  }
}
